// 12-month sparkline of reserve-fund balance movements.
//
// Owned by task 5.2 of the dashboard-redesign spec (Requirements 7.3, 7.4).
// See:
//   - `.kiro/specs/dashboard-redesign/design.md` §Widget table row 4
//     (`Widget_ReserveFundMovement`).
//   - `.kiro/specs/dashboard-redesign/requirements.md` §Requirement 7.
//   - `.kiro/steering/constitution.md` Rule 1 (integer pence) and Rule 2
//     (reserve-fund movements reconcile to the GL Reserve control account —
//     this widget is READ-ONLY presentation, never derivation).
//
// State model (see design.md §Widget state contracts):
//   - loading → <WidgetSkeleton>
//   - empty   → "No reserve fund movements"
//   - ready   → hand-rolled inline-SVG sparkline + min/max labels + trend arrow
//   - error   → <WidgetError>
//
// Rendering approach — no chart library.
// For each parsed movement point `p[i]`:
//   x = i * (viewWidth / (n - 1))
//   y = viewHeight - ((balance_i - min) / (max - min)) * viewHeight
// Degenerate paths:
//   - < 2 usable points  → render the "not enough data" note in place of
//     the sparkline (no chart, so slope calculations don't divide by zero).
//   - all points equal   → single horizontal line at viewHeight / 2 (avoids
//     the `max === min` division-by-zero and gives a truthful visual for a
//     flat balance).
//
// Colour choice:
//   - Flat / degenerate     → `#2563eb` (primary blue — the design-language
//     "primary" token; matches the Balance series colour in Req 7.3).
//   - Positive trend (last > first)  → `#16a34a` (Owed green).
//   - Negative trend (last < first)  → `#dc2626` (Owing red).
// Reserve fund balances are normally non-negative — zero-crossings are not
// modelled visually beyond the trend-colour choice.

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { useReserveFundPosition } from "../hooks/useReserveFundPosition";
import { formatMoneyGBP } from "../formatMoney";
import type { BuildingScope, ReserveFundPosition } from "../types";

/** Deep-link target for the KPI/sparkline (Req 7 · task 5.2 brief). */
const RESERVE_FUND_DEEP_LINK = "/dashboard/reserve-funds";

/** Empty-state copy (task 5.2 brief; design.md pairs with Req 7.4). */
const EMPTY_COPY = "No reserve fund movements";

/** Widget name — surfaces in the scoped error card title. */
const WIDGET_NAME = "Reserve Fund Movements";

/** Colour tokens — design-language.md §Core Palette + Semantic — money. */
const COLOUR_FLAT = "#2563eb";
const COLOUR_UP = "#16a34a";
const COLOUR_DOWN = "#dc2626";
const COLOUR_MUTED = "#6b7280";

/** SVG viewBox dimensions — width scales to container via `preserveAspectRatio`. */
const SVG_VIEW_WIDTH = 300;
const SVG_VIEW_HEIGHT = 80;
const SVG_STROKE_WIDTH = 2;

/** Public widget props — every dashboard widget takes exactly `scope`. */
export interface WidgetReserveFundMovementProps {
  scope: BuildingScope;
}

/** A single parsed point on the sparkline — integer pence + label. */
interface MovementPoint {
  balancePence: number;
  month: string | null;
}

/**
 * Defensively parse one entry from `movements12M`. The dashboard's
 * `ReserveFundMovement` type is `unknown` (owned by `reserve-funds`, not yet
 * published in `@akfdev-com/shared`), so we validate the fields we need:
 *
 *   - `balancePence` — a finite integer. If missing or malformed, drop the
 *     point rather than plotting NaN.
 *   - `month` — a string label, purely for tooltips/aria. Optional.
 *
 * Returning `null` on failure is deliberate — it lets `parseMovements`
 * filter the array cleanly without losing the loop's positional index.
 */
function parseMovement(raw: unknown): MovementPoint | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const balance = record.balancePence;
  if (typeof balance !== "number" || !Number.isFinite(balance)) return null;
  const month = typeof record.month === "string" ? record.month : null;
  return { balancePence: Math.trunc(balance), month };
}

/** Filter a raw `movements12M` array into a clean list of usable points. */
function parseMovements(raw: readonly unknown[]): MovementPoint[] {
  const out: MovementPoint[] = [];
  for (const entry of raw) {
    const parsed = parseMovement(entry);
    if (parsed) out.push(parsed);
  }
  return out;
}

/** Trend classification from first vs last point on the series. */
type Trend = "up" | "down" | "flat";

function classifyTrend(points: readonly MovementPoint[]): Trend {
  if (points.length < 2) return "flat";
  const first = points[0].balancePence;
  const last = points[points.length - 1].balancePence;
  if (last > first) return "up";
  if (last < first) return "down";
  return "flat";
}

function trendColour(trend: Trend): string {
  if (trend === "up") return COLOUR_UP;
  if (trend === "down") return COLOUR_DOWN;
  return COLOUR_FLAT;
}

function trendArrow(trend: Trend): "↑" | "↓" | "→" {
  if (trend === "up") return "↑";
  if (trend === "down") return "↓";
  return "→";
}

/**
 * Build the SVG polyline `points` attribute for a series of movements.
 * When `max === min` (flat series) every point maps to the vertical midpoint
 * so the resulting line is horizontal and truthfully flat.
 */
function buildPolylinePoints(points: readonly MovementPoint[]): string {
  const n = points.length;
  if (n < 2) return "";
  const min = points.reduce(
    (acc, p) => (p.balancePence < acc ? p.balancePence : acc),
    points[0].balancePence,
  );
  const max = points.reduce(
    (acc, p) => (p.balancePence > acc ? p.balancePence : acc),
    points[0].balancePence,
  );
  const range = max - min;
  const step = SVG_VIEW_WIDTH / (n - 1);
  const parts: string[] = [];
  for (let i = 0; i < n; i += 1) {
    const x = i * step;
    const y =
      range === 0
        ? SVG_VIEW_HEIGHT / 2
        : SVG_VIEW_HEIGHT -
          ((points[i].balancePence - min) / range) * SVG_VIEW_HEIGHT;
    parts.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  return parts.join(" ");
}

/**
 * Ready-state sparkline. Split out so the parent stays a thin state-router
 * over `<WidgetFrame>`.
 */
function ReadyState({ data }: { data: ReserveFundPosition }): JSX.Element {
  const points = parseMovements(data.movements12M);
  const trend = classifyTrend(points);
  const colour = trendColour(trend);
  const arrow = trendArrow(trend);

  // Min/max are read straight off the parsed points so the labels always
  // agree with the polyline. Guarded on `points.length > 0` — the < 2
  // branch below renders a different UI entirely.
  const min = points.length
    ? points.reduce(
        (acc, p) => (p.balancePence < acc ? p.balancePence : acc),
        points[0].balancePence,
      )
    : 0;
  const max = points.length
    ? points.reduce(
        (acc, p) => (p.balancePence > acc ? p.balancePence : acc),
        points[0].balancePence,
      )
    : 0;

  return (
    <div
      data-testid="widget-reserve-fund-movement-ready"
      className="w-full h-full flex flex-col gap-2 px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <div
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: COLOUR_MUTED }}
        >
          {WIDGET_NAME}
        </div>
        <span
          data-testid="widget-reserve-fund-movement-trend"
          aria-label={`Trend ${trend}`}
          className="text-sm font-semibold"
          style={{ color: colour }}
        >
          {arrow}
        </span>
      </div>

      {points.length < 2 ? (
        <div
          data-testid="widget-reserve-fund-movement-insufficient"
          className="flex items-center justify-center flex-1 text-xs"
          style={{ color: COLOUR_MUTED, minHeight: SVG_VIEW_HEIGHT }}
        >
          Not enough data to draw a trend line
        </div>
      ) : (
        <>
          <div
            className="flex items-center justify-between text-xs"
            style={{ color: COLOUR_MUTED }}
          >
            <span data-testid="widget-reserve-fund-movement-max">
              Max {formatMoneyGBP(max)}
            </span>
            <span data-testid="widget-reserve-fund-movement-min">
              Min {formatMoneyGBP(min)}
            </span>
          </div>

          <svg
            data-testid="widget-reserve-fund-movement-sparkline"
            role="img"
            aria-label={`Reserve fund balance over ${points.length} months, trend ${trend}`}
            viewBox={`0 0 ${SVG_VIEW_WIDTH} ${SVG_VIEW_HEIGHT}`}
            preserveAspectRatio="none"
            className="w-full"
            style={{ height: SVG_VIEW_HEIGHT }}
          >
            <polyline
              points={buildPolylinePoints(points)}
              fill="none"
              stroke={colour}
              strokeWidth={SVG_STROKE_WIDTH}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </>
      )}

      <Link
        to={RESERVE_FUND_DEEP_LINK}
        data-testid="widget-reserve-fund-movement-link"
        className="text-xs hover:underline"
        style={{ color: COLOUR_FLAT }}
      >
        View reserve fund →
      </Link>
    </div>
  );
}

/**
 * Fallback rendered by the ErrorBoundary when a descendant throws during
 * render. The `errorId` marks this as a client-side render error, distinct
 * from a fetch-layer error (which carries a UUID from the hook).
 */
function renderBoundaryFallback(): JSX.Element {
  return (
    <WidgetError
      errorId="widget-reserve-fund-movement-render-error"
      name={WIDGET_NAME}
    />
  );
}

/**
 * The public widget. Passes `scope` to the hook, hands the result to
 * `<WidgetFrame>` for lifecycle routing, and only builds the ready layout
 * when the hook has data.
 */
export function Widget_ReserveFundMovement({
  scope,
}: WidgetReserveFundMovementProps): JSX.Element {
  const result = useReserveFundPosition(scope);

  return (
    <ErrorBoundary fallback={renderBoundaryFallback}>
      <WidgetFrame
        result={result}
        name={WIDGET_NAME}
        emptyCopy={EMPTY_COPY}
        skeletonHeight={180}
      >
        {result.status === "ready" ? <ReadyState data={result.data} /> : null}
      </WidgetFrame>
    </ErrorBoundary>
  );
}

export default Widget_ReserveFundMovement;
